import validGenes from 'src/constants/genes.json'

// Which dropped files on the Upload page are result files from a tumour test done
// elsewhere (somatic result upload) rather than a gene list.
//
// An archive, a .tsv or a .json file is never a gene list, so it goes to the result
// upload by its name. A .csv or .txt file is read: it stays a gene list unless it is
// a table (two or more lines, one of them with two or more fields) in which fewer
// than half of the entries are known gene symbols. A list the gene-panel parser
// accepts has only known genes, so it always stays a gene list, and so does one
// with a few typos (the parser then names them). The dedicated result upload sends
// every file to the server without this check.

const RESULT_BY_NAME = /\.(zip|tar|tgz|tar\.gz|tbz2?|tar\.bz2|tar\.xz|txz|tsv|json)$/i
const MAYBE_GENE_LIST = /\.(txt|csv)$/i

// The head read when deciding; a gene list is far smaller than this.
const SNIFF_BYTES = 64 * 1024

let geneSet = null
const knownGenes = () => {
  if (!geneSet) geneSet = new Set(validGenes)
  return geneSet
}

export const isResultFileName = (name) => RESULT_BY_NAME.test(String(name || ''))

// The entries the gene-panel parser reads (UnifiedFileUpload): split on new lines
// and commas, trimmed, empty ones dropped.
const geneListEntries = (text) =>
  text
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean)

export const looksLikeGeneList = (text) => {
  const content = String(text || '')
  const entries = geneListEntries(content)
  if (!entries.length) return true
  const lines = content.split(/\r?\n/).filter((line) => line.trim())
  const table =
    lines.length >= 2 && lines.some((line) => line.split(/[,\t]/).filter((field) => field.trim()).length >= 2)
  if (!table) return true
  const genes = knownGenes()
  const known = entries.filter((entry) => genes.has(entry)).length
  return known / entries.length >= 0.5
}

const readHead = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => resolve(event.target.result)
    reader.onerror = (error) => reject(error)
    reader.readAsText(file.size > SNIFF_BYTES ? file.slice(0, SNIFF_BYTES) : file)
  })

// True when a dropped file belongs to the result upload.
export const isResultFile = async (file) => {
  if (isResultFileName(file?.name)) return true
  if (!MAYBE_GENE_LIST.test(String(file?.name || ''))) return false
  try {
    return !looksLikeGeneList(await readHead(file))
  } catch (error) {
    // Unreadable here: the gene-panel path reads it as before.
    return false
  }
}
